import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { guestHref } from "@/lib/guest";
import { publicUrl } from "@/lib/https";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import QRCode from "qrcode";
import api, { formatApiError } from "@/lib/api";
import { money, asset } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bell, Droplets, Receipt, X, Plus, Minus } from "lucide-react";

export default function ScanOrder() {
  const { tableId } = useParams();
  const [cart, setCart] = useState([]);
  const [cust, setCust] = useState({ name: "", phone: "" });
  const [sent, setSent] = useState(null);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["scan-table", tableId],
    queryFn: async () => (await api.get(`/public/table/${tableId}`)).data,
  });

  const cats = data?.categories || [];
  const sym = data?.currency_symbol || "₹";
  const filtered = useMemo(() => (data?.items || []).filter((m) =>
    (cat === "all" || m.category_id === cat) && (!search || m.name.toLowerCase().includes(search.toLowerCase()))), [data?.items, cat, search]);
  const items = data?.items || [];

  const add = (m) => setCart((c) => {
    const ex = c.find((x) => x.menu_item_id === m.id);
    if (ex) return c.map((x) => x.menu_item_id === m.id ? { ...x, qty: x.qty + 1 } : x);
    return [...c, { menu_item_id: m.id, name: m.name, price: m.price, qty: 1, tax_rate: m.tax_rate, addons: [] }];
  });
  const chg = (id, d) => setCart((c) => c.map((x) => x.menu_item_id === id ? { ...x, qty: Math.max(0, x.qty + d) } : x).filter((x) => x.qty > 0));
  const total = cart.reduce((s, i) => s + i.price * i.qty * (1 + (i.tax_rate || 5) / 100), 0);

  const call = async (type) => {
    try {
      await api.post(`/public/tables/${tableId}/call`, { type });
      toast.success({ waiter: "Waiter called", water: "Water requested", bill: "Bill requested", cancel: "Call cancelled" }[type] || "Sent");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const place = async () => {
    if (!cart.length) return toast.error("Add dishes first");
    try {
      const { data: order } = await api.post(`/public/table/${tableId}/orders`, {
        items: cart, customer_name: cust.name, customer_phone: cust.phone,
      });
      setSent(order);
      setCart([]);
      toast.success(`${order.order_number} sent to kitchen`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not place order"); }
  };

  if (isLoading) return <div className="min-h-screen grid place-items-center text-neutral-400">Loading menu…</div>;
  if (!data?.table) return <div className="min-h-screen grid place-items-center">Table not found</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-32" data-testid="scan-order">
      <div className="bg-[var(--kul-charcoal)] text-white p-5">
        <div className="text-xs uppercase tracking-widest text-white/60">Scan & Order</div>
        <h1 className="font-display text-2xl font-800 mt-1">{data.restaurant_name}</h1>
        <p className="text-white/70 text-sm mt-1">Table {data.table.name} · tap dishes, call staff anytime</p>
      </div>

      <div className="grid grid-cols-4 gap-2 p-4">
        {[
          { t: "waiter", icon: Bell, l: "Waiter" },
          { t: "water", icon: Droplets, l: "Water" },
          { t: "bill", icon: Receipt, l: "Bill" },
          { t: "cancel", icon: X, l: "Cancel" },
        ].map((b) => (
          <button key={b.t} onClick={() => call(b.t)} data-testid={`call-${b.t}`}
            className="kul-card p-3 text-center text-xs font-semibold active:scale-95">
            <b.icon className="w-5 h-5 mx-auto mb-1 text-[var(--kul-primary)]" />{b.l}
          </button>
        ))}
      </div>

      {sent && (
        <div className="mx-4 mb-3 kul-card p-4 text-sm">
          <div className="font-semibold">{sent.order_number} sent</div>
          <div className="text-neutral-500">Kitchen is preparing. You can add more items below.</div>
        </div>
      )}

      <div className="px-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search menu…" className="h-11 bg-white" />
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          <button onClick={() => setCat("all")} className={`px-3 py-1.5 rounded-full text-xs ${cat === "all" ? "bg-[var(--kul-primary)] text-white" : "bg-white border"}`}>All</button>
          {cats.map((c) => (
            <button key={c.id} onClick={() => setCat(c.id)} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${cat === c.id ? "bg-[var(--kul-primary)] text-white" : "bg-white border"}`}>{c.name}</button>
          ))}
        </div>
      </div>

      <div className="p-4 grid grid-cols-2 gap-3">
        {filtered.map((m) => (
          <button key={m.id} onClick={() => add(m)} className="kul-card text-left overflow-hidden" data-testid={`scan-item-${m.id}`}>
            <div className="h-24 bg-neutral-100">{m.image && <img src={asset(m.image)} alt="" className="w-full h-full object-cover" />}</div>
            <div className="p-2.5">
              <div className="text-sm font-medium line-clamp-2">{m.name}</div>
              <div className="text-[var(--kul-primary)] font-semibold mt-1">{money(m.price, sym)}</div>
            </div>
          </button>
        ))}
      </div>

      {!!cart.length && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t p-4 shadow-lg">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <Input placeholder="Your name" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} />
            <Input placeholder="Phone" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} />
          </div>
          {cart.map((it) => (
            <div key={it.menu_item_id} className="flex items-center gap-2 text-sm py-1">
              <span className="flex-1">{it.name}</span>
              <button onClick={() => chg(it.menu_item_id, -1)}><Minus className="w-4 h-4" /></button>
              <span>{it.qty}</span>
              <button onClick={() => chg(it.menu_item_id, 1)}><Plus className="w-4 h-4" /></button>
            </div>
          ))}
          <Button onClick={place} className="w-full h-11 mt-2 bg-[var(--kul-primary)] text-white" data-testid="scan-place">
            Send to kitchen · {money(total, sym)}
          </Button>
        </div>
      )}
    </div>
  );
}

export async function tableQrDataUrl(tableId, slug) {
  const path = guestHref(slug, `/scan/${tableId}`);
  return QRCode.toDataURL(publicUrl(path), { width: 280, margin: 1 });
}
